import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useSelector } from 'react-redux';
import { useFormikContext } from 'formik';
import { Box, Icon, Stack, Text, Flex } from '@tonic-ui/react';
import {
  DropdownMenu,
  FieldTextLabel,
  FormContainer,
  InputField,
  supportFormValidation,
  TextareaField,
  Tags,
  SubmitButton,
} from '../../components/forms';
import { getC1UserDetails, getCommonType } from '../../store';
import { getAllTimezones } from '../../utils/timezone';
import RadioButtonGroup from '../../components/forms/radio-button-group';
import { useAlert } from '../../components/alert';

type SupportAuthenticatedFormProps = {
  onSubmit: (supportData) => Promise<void>;
};

const SupportFormFields = ({ locale = 'en' }: { locale: 'en' | 'ja' }): JSX.Element => {
  const { t } = useTranslation('', { keyPrefix: 'support.supportPage' });
  const { values, isValid, dirty, errors } = useFormikContext<SupportFormValues>();
  const timeZones: { name: string; id: string }[] = getAllTimezones();
  const showPhoneFields = values?.contactMethod === 'phone';
  // NOTE: Not needed for the first iteration as EWS is the only service available in GovCloud.
  // const showServices = ['threat', 'technical'].includes(values?.issueType);
  const { showWarning, closeWarning } = useAlert();

  const contactMethods = [
    { label: t('contactOptions.email'), value: 'email' },
    { label: t('contactOptions.phone'), value: 'phone' },
  ];

  const issueTypeList = [
    {
      id: 'subscription',
      name: t('caseTypeMap.subscription'),
    },
    {
      id: 'userManagement',
      name: t('caseTypeMap.usermanagement'),
    },
    {
      id: 'threat',
      name: t('caseTypeMap.threat'),
    },
    {
      id: 'technical',
      name: t('caseTypeMap.technical'),
    },
  ];

  const severityLevelList = [
    {
      id: 'p4',
      name: t('severityLevelList.general'),
    },
    {
      id: 'p3',
      name: t('severityLevelList.low'),
    },
    {
      id: 'p2',
      name: t('severityLevelList.medium'),
    },
    {
      id: 'p1',
      name: t('severityLevelList.high'),
    },
  ];

  // TODO: Uncomment when more services are available in GovCloud
  // const serviceList = [
  //   {
  //     id: 'workloadSecurity',
  //     name: t('serviceList.workloadSecurity'),
  //   },
  //   {
  //     id: 'appSec',
  //     name: t('serviceList.applicationSecurity'),
  //   },
  //   {
  //     id: 'networkSec',
  //     name: t('serviceList.networkSecurity'),
  //   },
  //   {
  //     id: 'networkSecHostedInfra',
  //     name: t('serviceList.networkSecHostedInfra'),
  //   },
  //   {
  //     id: 'fileStorage',
  //     name: t('serviceList.fileStorage'),
  //   },
  //   {
  //     id: 'containerSec',
  //     name: t('serviceList.containerSecurity'),
  //   },
  //   {
  //     id: 'conformity',
  //     name: t('serviceList.conformity'),
  //   },
  //   {
  //     id: 'snyk',
  //     name: t('serviceList.snyk'),
  //   },
  // ];

  useEffect(() => {
    if (errors.issueDescription === 'validations.supportFormProblemDescription') {
      showWarning(t('supportFormProblemDescription'));
    } else {
      closeWarning();
    }
  }, [errors]);

  return (
    <>
      <Flex
        direction="column"
        flex="3"
        paddingTop="5x"
        paddingLeft="5x"
        height="100%"
        minWidth="700px"
      >
        <Stack spacing="4x">
          <Text size="2xl">{t('pageTitle')}</Text>
          <Text size="md">{t('pageDescription')}</Text>
        </Stack>
        <Box maxWidth="360px" mt="5x">
          <InputField
            id="name"
            type="text"
            name="name"
            label={t('nameTextLabel') as string}
            isRequired={true}
          />
        </Box>
        <Box maxWidth="360px" mt="5x">
          <InputField
            id="company-name"
            type="text"
            name="companyName"
            label={t('companyNameTextLabel') as string}
            isRequired={true}
          />
        </Box>
        <Box maxWidth="360px" mt="5x">
          <FieldTextLabel
            name={t('emailTextLabel') as string}
            isRequired={true}
            htmlFor="support-form-tags-field"
          />
          <Tags name="emails" placeholder="someone@example.com" id="support-form-tags-field" />
        </Box>
        <Box mt="5x">
          <DropdownMenu
            name="issueType"
            id="support-form-case-type-menu-selection"
            options={issueTypeList}
            label={t('caseTypeTextLabel')}
            placeholder={t('select')}
            isRequired={true}
            maxHeight={150}
          />
        </Box>
        {/* TODO: Uncomment when more security services are available in GovCloud
          showServices && (
            <Box mt="5x">
              <DropdownMenu
                name="service"
                id="support-form-service-menu-selection"
                options={serviceList}
                label={t('serviceTextLabel')}
                placeholder={t('select')}
                isRequired={true}
                maxHeight={150}
              />
          </Box>)
        */}
        <Stack maxWidth="640px" mt="5x" spacing="5x">
          <DropdownMenu
            name="severity"
            id="support-form-severity-menu-selection"
            options={severityLevelList}
            label={t('severityTextLabel')}
            placeholder={t('select')}
            isRequired={true}
            maxHeight={150}
          />
          <Stack>
            <TextareaField
              name="issueDescription"
              id="support-form-problem-description-field"
              label={t('problemDescriptionTextLabel') as string}
              isRequired={true}
              rows={3}
              cols={12}
            />
            <Flex direction="row" alignItems="center" marginTop="3x">
              <Icon icon="info" color="blue:50" marginRight="1x" />
              <Text>{t('problemDescriptionTextInfo')}</Text>
            </Flex>
          </Stack>
        </Stack>
        {locale !== 'ja' && (
          <>
            <Box mt="5x">
              <RadioButtonGroup
                label={t('contactMethodTextLabel') as string}
                name="contactMethod"
                radioOptions={contactMethods}
                isRequired={true}
              />
            </Box>
            {showPhoneFields && (
              <Box id="phoneDetailsBox" data-id="phone-details-box">
                <Stack direction="column" spacing="4x">
                  <Stack direction="row" spacing="6x" mt="5x">
                    <Box maxWidth="224px">
                      <InputField
                        id="phone-number"
                        data-id="support-form-phone-field"
                        type="text"
                        name="phoneNumber"
                        placeholder={t('phoneNumberPlaceholder') as string}
                        label={t('phoneNumberTextLabel') as string}
                        isRequired={true}
                      />
                    </Box>
                    <Box maxWidth="112px">
                      <InputField
                        id="phone-number-ext"
                        data-id="support-form-phone-ext-field"
                        type="text"
                        name="phoneNumberExt"
                        label={t('phoneNumberExtTextLabel') as string}
                      />
                    </Box>
                  </Stack>
                  <Box mt="3x">
                    <DropdownMenu
                      name="timezone"
                      id="support-form-timeZone-menu-selection"
                      options={timeZones}
                      label={t('timeZoneTextLabel')}
                      placeholder={t('select')}
                      isRequired={true}
                      maxHeight={150}
                    />
                  </Box>
                </Stack>
              </Box>
            )}
          </>
        )}
      </Flex>
      <Flex py="4x" pr="4x" justifyContent="flex-end">
        <SubmitButton
          variant="primary"
          id="support-form-submit"
          label={t('submitButtonText')}
          disabled={!(isValid && dirty)}
          width="160px"
        />
      </Flex>
    </>
  );
};

const SupportAuthenticatedForm = ({ onSubmit }: SupportAuthenticatedFormProps): JSX.Element => {
  const { userEmail, userLocale, userName, userTimeZone } = useSelector(
    (state) => ({
      userEmail: (getC1UserDetails(state, 'email') ??
        getC1UserDetails(state, 'wsEmailAddress')) as string,
      userLocale: getCommonType(state, 'locale') as string,
      userName: getC1UserDetails(state, 'name') as string,
      userTimeZone: (getC1UserDetails(state, 'timezone') ??
        getC1UserDetails(state, 'wsTimeZone')) as string,
    }),
    shallowEqual
  );

  const initialValues = {
    name: userName,
    emails: [userEmail],
    companyName: '',
    issueType: '',
    // service: '',
    severity: '',
    issueDescription: '',
    contactMethod: 'email',
    phoneNumber: '',
    phoneNumberExt: '',
    timezone: userTimeZone,
  };

  return (
    <FormContainer
      name="supportFormAuthGovCloud"
      id="support-form-authenticated-govcloud"
      data-id="support-form-authenticated-govcloud"
      validateOnBlur={true}
      validateOnChange={true}
      validateOnMount={true}
      enableReinitialize={true}
      initialValues={initialValues}
      customValidation={supportFormValidation}
      submitAction={async ({ setSubmitting, resetForm, ...supportRequestData }) =>
        onSubmit(supportRequestData)
      }
    >
      <SupportFormFields locale={userLocale as 'en' | 'ja'} />
    </FormContainer>
  );
};

SupportAuthenticatedForm.displayName = 'SupportAuthenticatedForm';

export default SupportAuthenticatedForm;
